import React, { useState } from 'react';
import { Card, Button, Badge, Icon, Avatar } from '../ui';
import { AlertDialog } from '../ui/radix/AlertDialog';

interface PaymentMethod {
  id: string;
  type: 'card' | 'bank';
  name: string;
  last4: string;
  isDefault: boolean;
}

interface PaymentMethodsListProps {
  paymentMethods: PaymentMethod[];
  onAddNew: () => void;
  onRemove: (id: string) => void;
  onSetDefault: (id: string) => void;
  isLoading?: boolean;
}

const PaymentMethodsList: React.FC<PaymentMethodsListProps> = ({
  paymentMethods,
  onAddNew,
  onRemove,
  onSetDefault,
  isLoading = false,
}) => {
  const [methodToDelete, setMethodToDelete] = useState<PaymentMethod | null>(null);

  if (!paymentMethods || paymentMethods.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
        <Icon name="card" size={48} style={{ color: 'var(--ink-4)' }} />
        <p style={{ fontWeight: 600, fontSize: 15, margin: 0 }}>No payment methods found</p>
        <p className="muted" style={{ margin: 0, textAlign: 'center' }}>
          Add a credit card or bank account to make deposits and withdrawals
        </p>
        <Button variant="primary" onClick={onAddNew} style={{ marginTop: 8 }}>
          <Icon name="plus" size={14} style={{ marginRight: 6 }} />
          Add Payment Method
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="pm-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, gap: 12 }}>
        <h3 style={{ fontWeight: 700, margin: 0, fontSize: 16 }}>Payment Methods</h3>
        <Button variant="secondary" size="sm" onClick={onAddNew}>
          <Icon name="plus" size={14} style={{ marginRight: 6 }} />
          Add New
        </Button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {paymentMethods.map((method) => (
          <Card key={method.id} pad hover className="pm-card" style={{ transition: 'all 0.2s' }}>
            <div className="pm-row" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <Avatar style={{ width: 48, height: 48, background: method.type === 'card' ? 'var(--brand-blue)' : 'var(--ink-2)', flexShrink: 0 }}>
                <Icon name={method.type === 'card' ? 'card' : 'building'} size={22} style={{ color: '#fff' }} />
              </Avatar>

              <div className="pm-main" style={{ flex: 1, minWidth: 0 }}>
                <div className="pm-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{method.name}</span>
                  {method.isDefault && <Badge tone="info" style={{ fontSize: 11 }}>Default</Badge>}
                </div>
                <span className="muted" style={{ fontSize: 14 }}>•••• {method.last4}</span>
              </div>

              <div className="pm-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {!method.isDefault && (
                  <Button variant="ghost" size="sm" onClick={() => onSetDefault(method.id)}>
                    Set Default
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMethodToDelete(method)}
                  style={{ color: 'var(--err)' }}
                >
                  <Icon name="trash" size={16} />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {paymentMethods.length > 0 && (
        <div style={{ marginTop: 24, padding: 20, background: 'rgba(59,130,246,0.05)', borderRadius: 12, border: '1px solid rgba(59,130,246,0.12)' }}>
          <p style={{ fontWeight: 500, margin: '0 0 6px' }}>Want to add another payment method?</p>
          <p className="muted" style={{ margin: '0 0 14px', fontSize: 13 }}>
            You can add credit cards, debit cards, or bank accounts to easily deposit or withdraw funds.
          </p>
          <Button variant="primary" size="sm" onClick={onAddNew}>
            <Icon name="plus" size={14} style={{ marginRight: 6 }} />
            Add New Payment Method
          </Button>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog
        open={!!methodToDelete}
        onOpenChange={(o) => { if (!o) setMethodToDelete(null); }}
        title="Delete Payment Method"
        description={`Are you sure you want to delete ${methodToDelete?.name}? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (methodToDelete) onRemove(methodToDelete.id);
          setMethodToDelete(null);
        }}
        destructive
      />
    </div>
  );
};

export default PaymentMethodsList;
